import { env } from "@/env";
import ky, { type Options } from "ky";
import type { ZodType } from "zod";

const isServer = typeof window === "undefined";

const _nativeJson = Response.prototype.json;

Response.prototype.json = function <T>(): Promise<T> {
  const clone = this.clone();
  return _nativeJson.call(clone) as Promise<T>;
};

const kyClient = ky.create({
  prefixUrl: isServer ? env.VITE_LOCAL_API_URL : env.VITE_API_URL,
  credentials: "include",
  timeout: 30000,
});

async function handleRequest<T>(requestFn: () => Promise<T>): Promise<T> {
  if (process.env.NODE_ENV === "development") {
    const waitMs = Math.floor(Math.random() * 400) + 100;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }

  return await requestFn();
}

async function requestWithSchema<T>(
  method: "get" | "post" | "put" | "delete",
  url: string,
  schema: ZodType<T>,
  opts: Options = {}
): Promise<T> {
  return handleRequest(async () => {
    const response = await kyClient[method](url, {
      ...opts,
    });
    const raw = await response.json<unknown>();
    return schema.parse(raw);
  });
}

async function requestWithoutSchema(
  method: "post" | "put" | "delete",
  url: string,
  opts: Options = {}
): Promise<void> {
  return handleRequest(async () => {
    await kyClient[method](url, {
      ...opts,
    });
  });
}

type GetParams<T> = {
  url: string;
  schema: ZodType<T>;
  options?: Omit<Options, "json">;
};

type MutationParamsWithSchema<T> = {
  url: string;
  schema: ZodType<T>;
  body?: unknown;
  options?: Omit<Options, "json">;
};

type MutationParamsWithoutSchema = {
  url: string;
  schema?: undefined;
  body?: unknown;
  options?: Omit<Options, "json">;
};

type DeleteParamsWithSchema<T> = {
  url: string;
  schema: ZodType<T>;
  options?: Omit<Options, "json">;
};

type DeleteParamsWithoutSchema = {
  url: string;
  schema?: undefined;
  options?: Omit<Options, "json">;
};

interface ApiType {
  get<T>(params: GetParams<T>): Promise<T>;

  post<T>(params: MutationParamsWithSchema<T>): Promise<T>;
  post(params: MutationParamsWithoutSchema): Promise<void>;

  put<T>(params: MutationParamsWithSchema<T>): Promise<T>;
  put(params: MutationParamsWithoutSchema): Promise<void>;

  delete<T>(params: DeleteParamsWithSchema<T>): Promise<T>;
  delete(params: DeleteParamsWithoutSchema): Promise<void>;
}

export const api: ApiType = {
  async get<T>({ url, schema, options }: GetParams<T>): Promise<T> {
    return requestWithSchema("get", url, schema, options);
  },

  async post<T>(
    params: MutationParamsWithSchema<T> | MutationParamsWithoutSchema
  ): Promise<T | void> {
    const { url, schema, body, options } = params;
    if (!schema) {
      return requestWithoutSchema("post", url, { ...options, json: body });
    }
    return requestWithSchema("post", url, schema, { ...options, json: body });
  },

  async put<T>(
    params: MutationParamsWithSchema<T> | MutationParamsWithoutSchema
  ): Promise<T | void> {
    const { url, schema, body, options } = params;
    if (!schema) {
      return requestWithoutSchema("put", url, { ...options, json: body });
    }
    return requestWithSchema("put", url, schema, { ...options, json: body });
  },

  async delete<T>(
    params: DeleteParamsWithSchema<T> | DeleteParamsWithoutSchema
  ): Promise<T | void> {
    const { url, schema, options } = params;
    if (!schema) {
      return requestWithoutSchema("delete", url, options);
    }
    return requestWithSchema("delete", url, schema, options);
  },
};
